import { faker } from '@faker-js/faker';

// Generate a random ID string
function generateId(): string {
  return faker.string.alphanumeric(20);
}

// Get fields for different object types
export function getObjectFields(objectType: string): string[] {
  switch (objectType) {
    case 'students':
      return ['id', 'name', 'email', 'studentIdNo', 'mobileNumber', 'bloodGroup', 'status', 'stream'];
    case 'employees':
      return ['id', 'name', 'email', 'employeeId', 'department', 'position', 'joiningDate', 'salary'];
    case 'courses':
      return ['id', 'title', 'description', 'duration', 'price', 'instructor', 'category'];
    case 'modules':
      return ['id', 'title', 'courseId', 'duration', 'order', 'description'];
    case 'users':
      return ['id', 'name', 'email', 'role', 'status', 'lastLogin'];
    default:
      return ['id', 'name', 'description', 'createdAt', 'updatedAt'];
  }
}

// Generate a mock object based on object type
export function generateMockObject(objectType: string): any {
  const baseObject = {
    id: generateId(),
    createdAt: faker.date.recent().toISOString(),
    updatedAt: faker.date.recent().toISOString(),
  };

  switch (objectType) {
    case 'students':
      return {
        ...baseObject,
        name: faker.person.fullName(),
        email: faker.internet.email(),
        mobileNumber: faker.phone.number(),
        studentIdNo: `T${faker.number.int({min: 10000, max: 99999})}`,
        bloodGroup: faker.helpers.arrayElement(['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']),
        status: faker.helpers.arrayElement(['Active', 'Inactive', 'Suspended']),
        permanentPinCode: faker.location.zipCode(),
        presentPinCode: faker.location.zipCode(),
        permanentFullAddress: faker.location.streetAddress(),
        nationality: 'Indian',
        maritalStatus: faker.helpers.arrayElement(['Single', 'Married']),
        religion: faker.helpers.arrayElement(['HINDU', 'MUSLIM', 'CHRISTIAN', 'OTHER']),
        permanentDistrict: faker.location.city(),
        aadharNumber: faker.number.int({min: 100000000000, max: 999999999999}).toString(),
        emailId: faker.internet.email(),
        stream: faker.helpers.arrayElement(['Computer Science', 'Mechanical', 'Electrical', 'Civil']),
        graduation: faker.helpers.arrayElement(['B.Tech', 'M.Tech', 'BCA', 'MCA']),
        yearOfPassedOut: faker.date.past().getFullYear().toString(),
        fatherName: faker.person.fullName(),
        referencePerson: faker.person.fullName(),
        feeInfo: {
          feeTotal: faker.number.int({min: 5000, max: 50000}),
          feeDue: faker.number.int({min: 0, max: 10000}),
          feePaid: faker.number.int({min: 5000, max: 40000}),
        }
      };
    case 'employees':
      return {
        ...baseObject,
        name: faker.person.fullName(),
        email: faker.internet.email(),
        employeeId: `EMP${faker.number.int({min: 1000, max: 9999})}`,
        department: faker.helpers.arrayElement(['HR', 'Engineering', 'Sales', 'Marketing', 'Finance']),
        position: faker.person.jobTitle(),
        joiningDate: faker.date.past().toISOString(),
        salary: faker.number.int({min: 30000, max: 150000}),
        phoneNumber: faker.phone.number(),
        address: faker.location.streetAddress(),
        city: faker.location.city(),
      };
    case 'courses':
      return {
        ...baseObject,
        title: faker.company.catchPhrase(),
        description: faker.lorem.paragraph(),
        duration: `${faker.number.int({min: 1, max: 12})} months`,
        price: faker.number.int({min: 5000, max: 50000}),
        instructor: faker.person.fullName(),
        category: faker.helpers.arrayElement(['Development', 'Design', 'Marketing', 'Business']),
        enrollments: faker.number.int({min: 5, max: 100}),
        rating: faker.number.float({min: 3, max: 5, fractionDigits: 1}),
      };
    case 'modules':
      return {
        ...baseObject,
        title: faker.company.buzzPhrase(),
        courseId: generateId(),
        duration: `${faker.number.int({min: 1, max: 4})} weeks`,
        order: faker.number.int({min: 1, max: 10}),
        description: faker.lorem.paragraph(),
        topics: Array(faker.number.int({min: 3, max: 8}))
          .fill(null)
          .map(() => faker.company.buzzPhrase()),
      };
    case 'users':
      return {
        ...baseObject,
        name: faker.person.fullName(),
        email: faker.internet.email(),
        role: faker.helpers.arrayElement(['Admin', 'Teacher', 'Student', 'Manager']),
        status: faker.helpers.arrayElement(['Active', 'Inactive']),
        lastLogin: faker.date.recent().toISOString(),
        avatar: faker.image.avatar(),
      };
    default:
      return {
        ...baseObject,
        name: faker.company.buzzPhrase(),
        description: faker.lorem.sentence(),
      };
  }
}

// Generate a collection of mock data
export function generateMockData(objectType: string, count: number): any[] {
  return Array(count)
    .fill(null)
    .map(() => generateMockObject(objectType));
}